(function () {
  'use strict';

  var API = 'https://app.lensadvizor.com/api';
  var PREPARING = 'la-flow-preparing';
  var STEP_TIMEOUT = 12000;

  var TYPE_DESCRIPTIONS = {
    'Single Vision': 'For distance or nearby vision correction.',
    Progressive:
      'For both near, intermediate and distance corrections in one, so you don’t need to switch frames.'
  };

  var COATING_RULES = [
    [/anti-?reflect|anti-?glare|\bar\b/i, 'Reduces reflections and glare from screens, headlights and overhead lighting.'],
    [/blue ?light/i, 'Filters high-energy blue light emitted by screens and LED lighting.'],
    [/anti-?fog/i, 'Resists condensation during temperature changes and mask wear.'],
    [/photochromic|transition|dimmer/i, 'Darkens automatically in UV light and returns to clear indoors.'],
    [/polari/i, 'Eliminates reflected glare from roads, water and snow. Recommended for outdoor use.'],
    [/scratch/i, 'Hardened surface layer for improved resistance to daily wear and scratching.'],
    [/\buv\b/i, 'Blocks UVA and UVB rays for full-spectrum ultraviolet protection.'],
    [/premium|advanced|\bplus\b/i, 'Includes the complete coating package for maximum clarity and durability.'],
    [/standard|basic|none|clear/i, 'Standard clear finish with no additional coatings.']
  ];

  function describeCoating(variant) {
    if (variant.description) return variant.description;

    var name = variant.name || '';

    for (var i = 0; i < COATING_RULES.length; i++) {
      if (COATING_RULES[i][0].test(name)) return COATING_RULES[i][1];
    }

    return '';
  }

  function describeType(type) {
    return type.description || TYPE_DESCRIPTIONS[type.name] || TYPE_DESCRIPTIONS[type.title] || '';
  }

  function engine() {
    return window.LensAdvizor;
  }

  function shop() {
    return window.Shopify && window.Shopify.shop;
  }

  function productConfig(productId) {
    var la = engine();
    if (!la || !la.products) return null;
    return la.products[String(productId)] || null;
  }

  function flowId(productId, variantId) {
    var config = productConfig(productId);
    if (!config) return null;

    var assigned = config.assigned_variants || {};
    var current = assigned[String(variantId)];
    if (current && current.collection_id) return current.collection_id;

    var withFlow = Object.keys(assigned).filter(function (key) {
      return assigned[key] && assigned[key].collection_id;
    })[0];

    if (withFlow) return assigned[withFlow].collection_id;

    return (config.collection_settings && config.collection_settings.collection_id) || null;
  }

  function isAvailable(productId, variantId) {
    return !!(
      engine() &&
      shop() &&
      productConfig(productId) &&
      flowId(productId, variantId) &&
      document.querySelector('#la-select-lenses-btn')
    );
  }

  function whenAvailable(productId, variantId, timeout) {
    var deadline = Date.now() + (timeout || 20000);

    return new Promise(function (resolve, reject) {
      (function poll() {
        if (isAvailable(productId, variantId)) return resolve(true);
        if (Date.now() > deadline) return reject(new Error('lens advisor did not initialise'));
        window.setTimeout(poll, 60);
      })();
    });
  }

  /*
    The full whenAvailable gate needs Lens Advizor's 2MB widget booted, which
    dominates time-to-interactive. The lens options themselves only need the
    shop domain (for the API header) and a flow id, so a theme-seeded flow id
    lets the options render while the widget is still downloading. The widget
    is only truly required at the prescription step, where whenEngineReady
    guards the hand-over.
  */
  function whenShopReady(timeout) {
    var deadline = Date.now() + (timeout || 10000);

    return new Promise(function (resolve, reject) {
      (function poll() {
        if (shop()) return resolve(true);
        if (Date.now() > deadline) return reject(new Error('shopify global not ready'));
        window.setTimeout(poll, 60);
      })();
    });
  }

  function whenEngineReady(timeout) {
    var deadline = Date.now() + (timeout || 20000);

    return new Promise(function (resolve, reject) {
      (function poll() {
        if (engine() && document.querySelector('#la-select-lenses-btn')) return resolve(true);
        if (Date.now() > deadline) return reject(new Error('lens advisor did not initialise'));
        window.setTimeout(poll, 60);
      })();
    });
  }

  function request(path, params) {
    var query = Object.keys(params)
      .filter(function (key) {
        return params[key] !== null && params[key] !== undefined;
      })
      .map(function (key) {
        return encodeURIComponent(key) + '=' + encodeURIComponent(params[key]);
      })
      .join('&');

    return fetch(API + path + '?' + query, {
      headers: { 'X-Shopify-Shop-Domain': shop() }
    }).then(function (response) {
      if (!response.ok) throw new Error('lens advisor ' + path + ' ' + response.status);
      return response.json();
    });
  }

  function normaliseItem(item) {
    return {
      id: item.id,
      title: item.name,
      description: item.description || '',
      image: (item.default_image && item.default_image.src) || '',
      price: item.price,
      groupTitle: item.group_title || '',
      variants: (item.variants || []).map(function (variant) {
        return {
          id: variant.variant_id,
          title: variant.name,
          description: describeCoating(variant),
          price: variant.price
        };
      })
    };
  }

  function loadConfig(productId, variantId, seedFlow) {
    var flow = seedFlow || flowId(productId, variantId);

    return request('/collection/' + flow + '/prescription-types/', {
      product_id: productId,
      api_version: 2
    }).then(function (payload) {
      var types = (payload.prescriptionTypes || []).map(function (type) {
        return {
          id: type.id,
          name: type.name,
          title: type.title,
          description: describeType(type)
        };
      });

      return Promise.all(
        types.map(function (type) {
          return request('/collection/' + flow + '/items/', {
            prescription_type: type.name,
            prescription_id: type.id,
            api_version: 2
          }).then(function (items) {
            type.items = (items || []).map(normaliseItem);
            return type;
          });
        })
      ).then(function () {
        return { flowId: flow, types: types };
      });
    });
  }

  function waitFor(check, timeout) {
    var deadline = Date.now() + (timeout || STEP_TIMEOUT);

    return new Promise(function (resolve, reject) {
      (function poll() {
        var result;
        try {
          result = check();
        } catch (error) {
          result = null;
        }

        if (result) return resolve(result);
        if (Date.now() > deadline) return reject(new Error('lens advisor step timed out'));
        window.setTimeout(poll, 120);
      })();
    });
  }

  function panel() {
    return document.querySelector('#lensadvizor-prescription-modal');
  }

  function clickable(element) {
    return (
      element.closest('[class*="presc"], label, button, [role="radio"], la-radio') || element
    );
  }

  function findByExactText(root, text) {
    var wanted = String(text).trim().toLowerCase();
    var all = root.querySelectorAll('*');

    for (var i = 0; i < all.length; i++) {
      if (all[i].children.length) continue;
      if ((all[i].textContent || '').trim().toLowerCase() === wanted) return all[i];
    }

    return null;
  }

  function openPanel() {
    if (panel()) return Promise.resolve(panel());

    var button = document.querySelector('#la-select-lenses-btn');
    if (!button) return Promise.reject(new Error('lens advisor button missing'));

    button.click();

    return waitFor(panel);
  }

  function chooseType(title) {
    return waitFor(function () {
      return findByExactText(panel(), title);
    }).then(function (element) {
      clickable(element).click();
      return waitFor(function () {
        return panel().querySelector('input[type="radio"]');
      });
    });
  }

  function chooseItem(itemId) {
    return waitFor(function () {
      return panel().querySelector('input[type="radio"][value="' + itemId + '"]');
    }).then(function (radio) {
      var label = panel().querySelector('label[for="' + radio.id + '"]') || radio.closest('label');
      (label || radio).click();
    });
  }

  function optionMatch(scope, selection) {
    var wanted = [selection.variantTitle, selection.variantId].filter(Boolean).map(String);
    var select = scope.querySelector('select');

    if (select) {
      var option = Array.prototype.slice.call(select.options).filter(function (candidate) {
        return wanted.indexOf(String(candidate.value)) !== -1;
      })[0];
      if (option) return { select: select, value: option.value };
    }

    var radios = scope.querySelectorAll('input[type="radio"]');
    for (var i = 0; i < radios.length; i++) {
      if (wanted.indexOf(String(radios[i].value)) !== -1) return { radio: radios[i] };
    }

    return null;
  }

  function optionApplied(scope, selection) {
    var match = optionMatch(scope, selection);
    if (!match) return false;
    return match.select ? match.select.value === match.value : match.radio.checked;
  }

  function chooseVariant(selection) {
    if (selection.variantCount <= 1) return Promise.resolve();
    if (!selection.variantTitle && !selection.variantId) return Promise.resolve();

    return waitFor(function () {
      return optionMatch(panel(), selection);
    }).then(function (match) {
      if (match.select) {
        match.select.value = match.value;
        match.select.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        var label = panel().querySelector('label[for="' + match.radio.id + '"]') || match.radio.closest('label');
        (label || match.radio).click();
      }

      return waitFor(function () {
        return optionApplied(panel(), selection);
      }, 2000);
    });
  }

  function advance() {
    if (atPrescriptionStep()) return;

    var button = Array.prototype.slice.call(panel().querySelectorAll('button')).find(function (candidate) {
      return /save and continue|continue|next/i.test(candidate.textContent || '');
    });

    if (!button) throw new Error('lens advisor continue button missing');
    button.click();
  }

  function content() {
    return document.querySelector('.la-prescription-modal-content');
  }

  function atPrescriptionStep() {
    var scope = content();
    if (!scope) return false;

    return !!(
      document.querySelector('[name="la-od_right-sph"]') ||
      document.querySelector('[name="la-pd-value"]') ||
      /reading strength|select your reading/i.test(scope.innerText || '')
    );
  }

  function reveal() {
    document.documentElement.classList.remove(PREPARING);
  }

  function neutralise() {
    var la = engine();
    if (!la || la._lensFlowPatched) return;

    var trapFocus = la.trapFocus.bind(la);
    la.trapFocus = function (element) {
      if (!element) return;
      trapFocus(element, false);
      if (la.boundTrapCallback) element.removeEventListener('keydown', la.boundTrapCallback);
    };

    la._lensFlowPatched = true;

    window._lensadvizorEmbed = window._lensadvizorEmbed || {};
    window._lensadvizorEmbed.postAddToCart = function () {
      document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('theme:cart:open', { bubbles: true }));
    };
  }

  function transplant(host) {
    var node = panel();
    if (!node || !host) return false;

    if (node.parentElement !== host) host.appendChild(node);
    document.documentElement.classList.add('lens-flow-inline');
    return true;
  }

  function restore() {
    var node = panel();

    if (node && node.parentElement && node.parentElement !== document.body) {
      document.body.appendChild(node);
    }

    document.documentElement.classList.remove('lens-flow-inline');
  }

  function closePanel() {
    document.documentElement.classList.add(PREPARING);

    if (!panel()) {
      restore();
      reveal();
      return Promise.resolve();
    }

    var close = document.querySelector('.la-prescription-modal-close');
    restore();
    if (close) close.click();

    return waitFor(function () {
      return !panel();
    }, 6000)
      .catch(function () {
        return true;
      })
      .then(function () {
        return new Promise(function (resolve) {
          window.setTimeout(resolve, 350);
        });
      })
      .then(reveal);
  }

  function onClose(callback) {
    document.addEventListener('LensAdvizor:selectLensModal:close', function () {
      restore();
      callback();
    });
  }

  function openAtPrescriptionStep(selection, host) {
    document.documentElement.classList.add(PREPARING);
    neutralise();

    return openPanel()
      .then(function () {
        return chooseType(selection.typeTitle);
      })
      .then(function () {
        return chooseItem(selection.itemId);
      })
      .then(function () {
        return chooseVariant(selection);
      })
      .then(function () {
        return new Promise(function (resolve) {
          window.setTimeout(resolve, 400);
        });
      })
      .then(function () {
        advance();
        return waitFor(atPrescriptionStep);
      })
      .then(function () {
        var moved = transplant(host);
        reveal();
        if (!moved) throw new Error('lens advisor content could not be moved inline');
      })
      .catch(function (error) {
        reveal();
        throw error;
      });
  }

  function field(name) {
    var scope = panel();
    return scope ? scope.querySelector('[name="' + name + '"]') : null;
  }

  function sameNumber(a, b) {
    var x = parseFloat(a);
    var y = parseFloat(b);
    return !isNaN(x) && !isNaN(y) && Math.abs(x - y) < 0.001;
  }

  function optionFor(select, value) {
    return (
      Array.prototype.slice.call(select.options).filter(function (option) {
        return option.value !== '' && sameNumber(option.value, value);
      })[0] || null
    );
  }

  function canChoose(name, value) {
    var select = field(name);
    return !!(select && optionFor(select, value));
  }

  function choose(name, value) {
    var select = field(name);
    if (!select || select.disabled) return false;

    var option = optionFor(select, value);
    if (!option) return false;

    select.value = option.value;
    select.dispatchEvent(new Event('input', { bubbles: true }));
    select.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function pickRadio(name, value) {
    var radios = panel().querySelectorAll('input[type="radio"][name="' + name + '"]');

    for (var i = 0; i < radios.length; i++) {
      var match = typeof value === 'string' && isNaN(parseFloat(value))
        ? radios[i].value === value
        : sameNumber(radios[i].value, value);

      if (match) {
        if (!radios[i].checked) radios[i].click();
        return true;
      }
    }

    return false;
  }

  function parseEye(raw) {
    var parts = String(raw || '').split(' | ');
    var eye = { sph: parts[0] || '', cyl: parts[1] || '', axis: '', add: '' };

    parts.slice(2).forEach(function (part) {
      if (part.charAt(0) === 'x') eye.axis = String(parseInt(part.slice(1), 10));
      else if (part.indexOf('ADD') === 0) eye.add = part.replace('ADD', '').trim();
    });

    return eye;
  }

  function parsePd(raw) {
    var parts = String(raw || '').split(' | ');

    if (/^Single PD/.test(parts[0])) return { type: 'Single PD', value: parts[1] };
    if (/^OD - /.test(parts[0])) {
      return {
        type: 'Dual PD',
        right: parts[0].replace('OD - ', ''),
        left: (parts[1] || '').replace('OS - ', '')
      };
    }

    return null;
  }

  function settle(ms) {
    return new Promise(function (resolve) {
      window.setTimeout(resolve, ms || 80);
    });
  }

  function fillPrescription(rx) {
    if (!panel() || !atPrescriptionStep()) return Promise.reject(new Error('prescription form not ready'));

    var readers = !!rx.diopters && !rx.od;
    var eyes = readers
      ? []
      : [
          { key: 'od_right', eye: parseEye(rx.od) },
          { key: 'os_left', eye: parseEye(rx.os) }
        ];
    var pd = parsePd(rx.pd);
    var strength = readers ? rx.diopters.split(' | ')[0] : '';
    var expected = [];
    var missing = [];

    if (readers) {
      var hasStrength = Array.prototype.some.call(
        panel().querySelectorAll('input[type="radio"][name="la-diopters"]'),
        function (radio) {
          return sameNumber(radio.value, strength);
        }
      );
      if (!hasStrength) missing.push('strength');
    }

    eyes.forEach(function (entry) {
      var name = 'la-' + entry.key;
      if (!canChoose(name + '-sph', entry.eye.sph)) missing.push(name + '-sph');
      if (entry.eye.cyl && !canChoose(name + '-cyl', entry.eye.cyl)) missing.push(name + '-cyl');
      if (field(name + '-add') && !canChoose(name + '-add', entry.eye.add)) missing.push(name + '-add');
    });

    if (pd && pd.type === 'Single PD' && field('la-pd-value') && !canChoose('la-pd-value', pd.value)) {
      missing.push('la-pd-value');
    }

    if (missing.length) {
      var early = new Error('prescription values not available for this lens');
      early.missing = missing;
      return Promise.reject(early);
    }

    var chain = Promise.resolve();

    if (readers) {
      chain = chain.then(function () {
        pickRadio('la-diopters', strength);
        expected.push({ radio: 'la-diopters', value: strength });
        return settle();
      });
    }

    eyes.forEach(function (entry) {
      var name = 'la-' + entry.key;

      chain = chain
        .then(function () {
          choose(name + '-sph', entry.eye.sph);
          expected.push({ name: name + '-sph', value: entry.eye.sph });
          if (entry.eye.cyl) {
            choose(name + '-cyl', entry.eye.cyl);
            expected.push({ name: name + '-cyl', value: entry.eye.cyl });
          }
          if (entry.eye.add && field(name + '-add')) {
            choose(name + '-add', entry.eye.add);
            expected.push({ name: name + '-add', value: entry.eye.add });
          }
          return settle();
        })
        .then(function () {
          if (!entry.eye.axis) return null;

          return waitFor(function () {
            var axis = field(name + '-axis');
            return axis && !axis.disabled;
          }, 2000)
            .then(function () {
              choose(name + '-axis', entry.eye.axis);
            })
            .catch(function () {})
            .then(function () {
              expected.push({ name: name + '-axis', value: entry.eye.axis });
            });
        });
    });

    if (pd && field('la-pd-value')) {
      chain = chain
        .then(function () {
          pickRadio('la-pd-field', pd.type);
          return settle(150);
        })
        .then(function () {
          if (pd.type === 'Single PD') {
            choose('la-pd-value', pd.value);
            expected.push({ name: 'la-pd-value', value: pd.value });
            return null;
          }

          return waitFor(function () {
            return field('la-pd-right') && field('la-pd-left');
          }, 2000)
            .then(function () {
              choose('la-pd-right', pd.right);
              choose('la-pd-left', pd.left);
            })
            .catch(function () {})
            .then(function () {
              expected.push({ name: 'la-pd-right', value: pd.right });
              expected.push({ name: 'la-pd-left', value: pd.left });
            });
        });
    }

    return chain
      .then(function () {
        return settle(300);
      })
      .then(function () {
        var wrong = expected.filter(function (item) {
          if (item.radio) {
            var checked = panel().querySelector('input[type="radio"][name="' + item.radio + '"]:checked');
            return !checked || !sameNumber(checked.value, item.value);
          }

          var select = field(item.name);
          return !select || !sameNumber(select.value, item.value);
        });

        if (wrong.length) {
          var error = new Error('prescription values did not stick');
          error.missing = wrong.map(function (item) {
            return item.name || item.radio;
          });
          throw error;
        }

        return true;
      });
  }

  function uploadState(name) {
    var scope = panel();
    if (!scope) return 'error';

    var errors = scope.querySelectorAll('.la-error');
    for (var i = 0; i < errors.length; i++) {
      if (/upload/i.test(errors[i].textContent || '')) return 'error';
    }

    var previews = scope.querySelectorAll('.la-file-preview-btn, #la-prescription-upload-message');
    for (var j = 0; j < previews.length; j++) {
      if ((previews[j].textContent || '').indexOf(name) !== -1) return 'ok';
    }

    var input = field('la-prescription-file');
    return input && !input.required ? 'ok' : '';
  }

  function attachPhoto(url) {
    var input = field('la-prescription-file');
    if (!input) return Promise.reject(new Error('photo field missing'));
    if (typeof DataTransfer === 'undefined') return Promise.reject(new Error('browser cannot attach files'));

    return fetch(url, { credentials: 'omit' })
      .then(function (response) {
        if (!response.ok) throw new Error('photo unavailable ' + response.status);

        var name = decodeURIComponent(response.headers.get('X-Rx-Filename') || 'prescription');
        return response.blob().then(function (blob) {
          return { blob: blob, name: name };
        });
      })
      .then(function (photo) {
        var target = field('la-prescription-file');
        if (!target) throw new Error('photo field missing');

        var file = new File([photo.blob], photo.name, { type: photo.blob.type, lastModified: Date.now() });
        var transfer = new DataTransfer();
        transfer.items.add(file);
        target.files = transfer.files;
        target.dispatchEvent(new Event('change', { bubbles: true }));

        return waitFor(function () {
          return uploadState(photo.name);
        }, 30000);
      })
      .then(function (state) {
        if (state !== 'ok') throw new Error('lens advisor rejected the photo');
        return true;
      });
  }

  var queue = Promise.resolve();

  function enqueue(task) {
    var run = queue.then(task, task);

    queue = run.catch(function () {});
    return run;
  }

  window.LensFlowBridge = {
    resolveFlowId: flowId,
    isAvailable: isAvailable,
    whenAvailable: whenAvailable,
    whenShopReady: whenShopReady,
    loadConfig: loadConfig,
    openAtPrescriptionStep: function (selection, host) {
      return enqueue(function () {
        return whenEngineReady().then(function () {
          return openAtPrescriptionStep(selection, host);
        });
      });
    },
    closePanel: function () {
      return enqueue(closePanel);
    },
    onClose: onClose,
    fillPrescription: function (rx) {
      return enqueue(function () {
        return fillPrescription(rx);
      });
    },
    attachPhoto: function (url) {
      return enqueue(function () {
        return attachPhoto(url);
      });
    },
    isPanelOpen: function () {
      return !!panel();
    }
  };
})();
